//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { facebookInstagramSettingsActions } from '@account-settings/redux/facebook-instagram/facebookInstagramSettingsActions';
import { getInstagramConnectionData } from '@account-settings/redux/facebook-instagram/selectors/getInstagramConnectionData';
import { getHasMultiHandleInstagramEnabled } from '@account-settings/redux/facebook-instagram/selectors/getMultiHandleFeature';
import { IConnectedBrandInstagramBusinessAccount } from '@apis/instagram-settings/gatewayInstagramSettingsApiModel';
import { isDefined } from '@utils/filterPredicates';

interface IUseConnectedInstagramAccount {
  connectedInstagramAccount:
    | IConnectedBrandInstagramBusinessAccount
    | undefined;
  isFetchingConnectedInstagramAccount: boolean;
}

export const useConnectedInstagramAccount =
  (): IUseConnectedInstagramAccount => {
    const dispatch = useDispatch();

    const {
      connectedInstagramAccount,
      facebookUserToken,
      isFetchingConnectedInstagramAccount,
      isFetchingFacebookUserToken,
    } = useSelector(getInstagramConnectionData);

    const isMutliHandleFeatureEnabled = useSelector(
      getHasMultiHandleInstagramEnabled,
    );

    const {
      fetchConnectedInstagramAccount,
      fetchConnectedMultiHanldeInstagramAccount,
      fetchFacebookUserToken,
    } = facebookInstagramSettingsActions;

    useEffect(() => {
      if (
        !isDefined(connectedInstagramAccount) &&
        !isFetchingConnectedInstagramAccount
      ) {
        if (isMutliHandleFeatureEnabled) {
          dispatch(fetchConnectedMultiHanldeInstagramAccount());
        } else {
          dispatch(fetchConnectedInstagramAccount());
        }
      }
    }, []);

    useEffect(() => {
      if (!isDefined(facebookUserToken) && !isFetchingFacebookUserToken) {
        dispatch(fetchFacebookUserToken());
      }
    }, []);

    return {
      isFetchingConnectedInstagramAccount:
        isFetchingConnectedInstagramAccount || isFetchingFacebookUserToken,
      connectedInstagramAccount: connectedInstagramAccount,
    };
  };
